import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import {
  format, addDays, addMonths, startOfWeek, startOfMonth, endOfMonth, endOfWeek,
  isSameMonth, isToday, parseISO, isPast,
} from "date-fns";
import { fr } from "date-fns/locale";
import {
  ChevronLeft, ChevronRight, Plus, CalendarClock, Video, MapPin, Phone, Trash2,
  CheckCircle2, X, Pencil, ExternalLink, CalendarDays, Clock, FileText, User,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import RdvFiche from "@/components/RdvFiche";
import { toast } from "sonner";

/* ---- Catégories d'agendas : 1 couleur = 1 calendrier (pastels du Design System) ---- */
const AGENDAS = {
  rdv: { label: "Rendez-vous clients", bg: "#DBEAFE", text: "#1D4ED8", border: "#93C5FD", dot: "#3B82F6" },
  taches: { label: "Tâches internes", bg: "#FEF3C7", text: "#B45309", border: "#FDE68A", dot: "#F59E0B" },
  cm: { label: "Community Management", bg: "#DCFCE7", text: "#15803D", border: "#86EFAC", dot: "#10B981" },
  graphisme: { label: "Graphisme", bg: "#FFEDD5", text: "#C2410C", border: "#FED7AA", dot: "#F97316" },
  web: { label: "Web Design", bg: "#F3E8FF", text: "#6B21A8", border: "#D8B4FE", dot: "#8B5CF6" },
  indispo: { label: "Indisponibilités", bg: "#F1F5F9", text: "#64748B", border: "#E2E8F0", dot: "#94A3B8" },
};
const TYPE_TO_AGENDA = { meeting: "rdv", internal_task: "taches", unavailable: "indispo", recurring_client_work: "cm", client_request: "cm", graphisme_production: "graphisme", shooting: "cm" };
const agendaOf = (b, clientById) => {
  if (TYPE_TO_AGENDA[b.type]) return TYPE_TO_AGENDA[b.type];
  if (b.type === "task") {
    const c = b.client_id && clientById[b.client_id];
    const svcs = c?.services?.length ? c.services : [c?.service_type];
    if (svcs?.includes("graphisme")) return "graphisme";
    if (svcs?.some((s) => ["site_web", "web"].includes(s))) return "web";
    return "cm";
  }
  return "cm";
};
const BLOCK_TYPE_LABELS = { recurring_client_work: "Community Management (travail récurrent)", internal_task: "Tâche interne", task: "Tâche client", meeting: "Rendez-vous", client_request: "Demande client", unavailable: "Indisponibilité", graphisme_production: "Production graphisme", shooting: "Shooting" };

const toMin = (hhmm) => { const [h, m] = hhmm.split(":").map(Number); return h * 60 + m; };
const toHHMM = (min) => `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(min % 60).padStart(2, "0")}`;
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const initials = (name = "") => name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "·";
const safeFmt = (raw, f) => { try { const d = parseISO(String(raw).slice(0, 10)); return isNaN(d) ? "" : format(d, f, { locale: fr }); } catch { return ""; } };
const HOUR_H = 46; // px par heure — compact pour tenir sur un écran

/* ---------- Bloc coloré (draggable) ---------- */
function BlockCard({ b, dayStart, ag, onOpen, onDragStart, onDragEnd, onResize, highlighted, dimmed }) {
  const [deltaMin, setDeltaMin] = useState(null);
  const top = ((b.start_min - dayStart) / 60) * HOUR_H;
  const height = Math.max(24, ((b.end_min - b.start_min + (deltaMin || 0)) / 60) * HOUR_H);
  const startResize = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const startY = e.clientY;
    const onMove = (ev) => setDeltaMin(Math.round(((ev.clientY - startY) / HOUR_H) * 60));
    const onUp = (ev) => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      setDeltaMin(null);
      const d = Math.round(((ev.clientY - startY) / HOUR_H) * 60);
      if (Math.abs(d) < 3) return;
      onResize(b, Math.max(b.start_min + 15, b.end_min + d));
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };
  return (
    <div
      data-testid={`agenda-block-${b.id}`}
      draggable
      onDragStart={(e) => onDragStart(e, b)}
      onDragEnd={onDragEnd}
      onClick={(e) => { e.stopPropagation(); onOpen(b); }}
      className={`absolute left-0.5 right-0.5 rounded-lg border-l-4 px-1.5 py-1 cursor-grab active:cursor-grabbing overflow-hidden hover:shadow-md transition-shadow group ${highlighted ? "ring-2 ring-[#0B132B]" : ""} ${dimmed ? "opacity-30" : ""}`}
      style={{
        top, height,
        background: b.booking_status === "provisoire"
          ? `repeating-linear-gradient(45deg, ${ag.bg}, ${ag.bg} 6px, ${ag.dot}33 6px, ${ag.dot}33 12px)`
          : ag.bg,
        borderColor: ag.dot,
        borderLeftStyle: b.booking_status === "provisoire" ? "dashed" : "solid",
      }}
      title={`${b.title} — glisser pour déplacer`}
    >
      <p className="text-[9px] font-semibold" style={{ color: ag.text }}>{toHHMM(b.start_min)} – {toHHMM(b.end_min)}</p>
      <p className="text-[11px] font-semibold text-[#0F172A] leading-tight truncate">{b.title}</p>
      {height > 40 && b.client?.name && <p className="text-[9px] truncate" style={{ color: ag.text }}>{b.client.name}</p>}
      <div onMouseDown={startResize} onClick={(e) => e.stopPropagation()} data-testid={`agenda-resize-${b.id}`}
        className="absolute bottom-0 left-0 right-0 h-2 cursor-ns-resize opacity-0 group-hover:opacity-100 flex items-end justify-center transition-opacity"
        title="Étirer pour modifier la durée">
        <span className="w-8 h-1 rounded-full mb-0.5" style={{ background: ag.dot }} />
      </div>
    </div>
  );
}

/* ---------- Mini calendrier mensuel ---------- */
function MiniCalendar({ cursor, selected, onPick }) {
  const mStart = startOfMonth(cursor);
  const days = [];
  let d = startOfWeek(mStart, { weekStartsOn: 1 });
  const end = endOfWeek(endOfMonth(cursor), { weekStartsOn: 1 });
  while (d <= end) { days.push(d); d = addDays(d, 1); }
  return (
    <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3" data-testid="mini-calendar">
      <div className="flex items-center justify-between mb-1.5">
        <p className="font-heading font-bold text-[13px] text-[#0F172A] capitalize">{format(cursor, "MMMM yyyy", { locale: fr })}</p>
        <div className="flex gap-0.5">
          <button data-testid="mini-cal-prev" onClick={() => onPick(addMonths(cursor, -1), true)} className="p-1 rounded-md hover:bg-neutral-100 text-neutral-500"><ChevronLeft className="w-3.5 h-3.5" /></button>
          <button data-testid="mini-cal-next" onClick={() => onPick(addMonths(cursor, 1), true)} className="p-1 rounded-md hover:bg-neutral-100 text-neutral-500"><ChevronRight className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      <div className="grid grid-cols-7 text-center text-[9px] font-semibold text-neutral-400 mb-0.5">
        {["L", "M", "M", "J", "V", "S", "D"].map((x, i) => <span key={i}>{x}</span>)}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const inMonth = isSameMonth(day, cursor);
          const sel = iso(day) === iso(selected);
          return (
            <button key={day.toISOString()} data-testid={`mini-cal-day-${iso(day)}`} onClick={() => onPick(day)}
              className={`h-7 text-[11px] rounded-full flex items-center justify-center transition-colors ${
                sel ? "bg-[#3B82F6] text-white font-bold shadow" : isToday(day) ? "bg-[#0B132B] text-white font-semibold" : inMonth ? "text-neutral-700 hover:bg-neutral-100" : "text-neutral-300"}`}>
              {format(day, "d")}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- Drawer détail (bloc ou RDV) ---------- */
function DetailDrawer({ detail, rdv, clientById, onClose, onEdit, onDeleteBlock, onCompleteTask, onReload, onOpenFiche }) {
  const navigate = useNavigate();
  const isRdv = !!rdv;
  const b = detail?.block;
  const appt = rdv || detail?.appointment;
  const title = isRdv ? rdv.title : b?.title;
  const client = detail?.client || (b?.client) || null;
  const ag = b ? AGENDAS[agendaOf(b, clientById)] : AGENDAS.rdv;
  const dateStr = isRdv ? rdv.date : b?.date;
  const startMin = isRdv ? rdv.start_min : b?.start_min;
  const durMin = isRdv ? (rdv.rdv_minutes || rdv.duration_minutes) : (b ? b.end_min - b.start_min : 0);
  const task = detail?.task;
  const [shooting, setShooting] = useState(null);
  useEffect(() => {
    setShooting(null);
    if (b?.type === "shooting" && b?.shooting_id) {
      api.get(`/content-piloting/shootings/${b.shooting_id}`).then((r) => setShooting(r.data)).catch(() => {});
    }
  }, [b?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleShot = async (item, shot) => {
    try {
      await api.patch(`/content-piloting/items/${item.id}`, { shot });
      setShooting((s) => s && { ...s, items: s.items.map((x) => (x.id === item.id ? { ...x, shot } : x)) });
      toast.success(shot ? `« ${item.title} » tourné — passe en production (créé, à finaliser)` : "Coche « tourné » retirée");
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!isRdv && !b) return null;
  return (
    <div className="fixed inset-y-0 right-0 w-full sm:w-[430px] bg-white border-l border-[#E2E8F0] shadow-2xl z-40 flex flex-col animate-in slide-in-from-right" data-testid="agenda-drawer">
      <div className="px-5 py-4 border-b border-neutral-100 flex items-start gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center border shrink-0" style={{ background: ag.bg, borderColor: ag.border }}>
          {isRdv ? <Video className="w-4 h-4" style={{ color: ag.text }} /> : <CalendarClock className="w-4 h-4" style={{ color: ag.text }} />}
        </div>
        <div className="flex-1 min-w-0">
          <span className="text-[9px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border" style={{ background: ag.bg, color: ag.text, borderColor: ag.border }} data-testid="drawer-type">
            {isRdv ? `RDV ${rdv.type_label || "client"}` : (BLOCK_TYPE_LABELS[b.type] || b.type)}
          </span>
          <h2 className="font-heading font-bold text-lg text-[#0F172A] mt-1.5 leading-tight" data-testid="drawer-title">{title}</h2>
        </div>
        <button onClick={onClose} data-testid="drawer-close" className="p-1.5 rounded-lg hover:bg-neutral-100 text-neutral-400"><X className="w-4 h-4" /></button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 text-[13px]">
        {(client || appt?.company || appt?.contact_name) && (
          <div className="flex items-center gap-2">
            <User className="w-4 h-4 text-neutral-400 shrink-0" />
            {client?.id ? (
              <Link to={`/clients/${client.id}`} className="font-semibold text-[#0369A1] hover:underline" data-testid="drawer-client">{client.name}</Link>
            ) : (
              <span className="font-semibold text-[#0F172A]">{appt?.company || appt?.contact_name}</span>
            )}
          </div>
        )}
        <div className="flex items-center gap-2 text-neutral-600">
          <CalendarDays className="w-4 h-4 text-neutral-400 shrink-0" />
          <span className="capitalize">{dateStr && safeFmt(dateStr, "EEEE d MMMM yyyy")}</span>
        </div>
        <div className="flex items-center gap-2 text-neutral-600">
          <Clock className="w-4 h-4 text-neutral-400 shrink-0" />
          <span>{toHHMM(startMin)} – {toHHMM(startMin + durMin)} ({durMin >= 60 ? `${Math.floor(durMin / 60)} h ${String(durMin % 60).padStart(2, "0")}` : `${durMin} min`})</span>
        </div>
        {appt?.visio_link && (
          <a href={appt.visio_link} target="_blank" rel="noreferrer" data-testid="drawer-visio"
            className="flex items-center gap-2 text-[#0369A1] hover:underline font-medium">
            <Video className="w-4 h-4 shrink-0" /> Visio : {appt.visio_link} <ExternalLink className="w-3 h-3" />
          </a>
        )}
        {appt?.type === "telephonique" && appt?.phone && (
          <p className="flex items-center gap-2 text-neutral-600"><Phone className="w-4 h-4 text-neutral-400 shrink-0" /> {appt.phone}</p>
        )}
        {appt?.type === "exterieur" && appt?.address && (
          <p className="flex items-center gap-2 text-neutral-600"><MapPin className="w-4 h-4 text-neutral-400 shrink-0" /> {appt.address}</p>
        )}
        {(b?.description || appt?.description) && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1 flex items-center gap-1.5"><FileText className="w-3 h-3" /> Description</p>
            <p className="text-neutral-600 whitespace-pre-line leading-relaxed">{b?.description || appt?.description}</p>
          </div>
        )}
        {appt?.report && (
          <div>
            <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Compte rendu</p>
            <p className="text-neutral-600 whitespace-pre-line leading-relaxed bg-neutral-50 border border-neutral-100 rounded-xl p-3" data-testid="drawer-report">{appt.report}</p>
          </div>
        )}
        {appt?.attachment && (
          <a href={appt.attachment} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[#0369A1] hover:underline text-xs">
            <FileText className="w-3.5 h-3.5" /> Pièce jointe
          </a>
        )}
        {task && (
          <div className="bg-neutral-50 border border-neutral-100 rounded-xl p-3" data-testid="drawer-task">
            <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1">Tâche liée</p>
            <div className="flex items-center gap-2">
              <p className="flex-1 font-medium text-[#0F172A] truncate">{task.title}</p>
              {!["finalise", "livre_client"].includes(task.status) && (
                <button onClick={() => onCompleteTask(task)} data-testid="drawer-task-done"
                  className="text-[11px] font-semibold text-[#15803D] hover:underline shrink-0">Marquer terminée</button>
              )}
            </div>
          </div>
        )}
        {shooting && (
          <div className="bg-teal-50/50 border border-teal-100 rounded-xl p-3" data-testid="drawer-shooting-items">
            <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-400 mb-1.5">
              Contenus à tourner — {shooting.items.filter((i) => i.shot).length}/{shooting.items.length} tournés
            </p>
            {shooting.items.length === 0 && <p className="text-xs text-neutral-400">Aucun contenu rattaché à ce shooting.</p>}
            <div className="space-y-1.5">
              {shooting.items.map((i) => (
                <label key={i.id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`drawer-shoot-item-${i.id}`}>
                  <input type="checkbox" className="rounded accent-teal-600" checked={!!i.shot}
                    onChange={(e) => toggleShot(i, e.target.checked)} data-testid={`drawer-shoot-check-${i.id}`} />
                  <span className={i.shot ? "line-through text-neutral-400" : "font-medium text-neutral-700"}>{i.title}</span>
                  {i.highlight_name && <span className="text-[9px] text-violet-500">{i.highlight_name}</span>}
                </label>
              ))}
            </div>
            <p className="text-[10px] text-neutral-400 mt-2">Cocher « Tourné » fait passer le contenu en production (créé, à finaliser).</p>
          </div>
        )}
      </div>

      <div className="border-t border-neutral-100 px-5 py-3 flex items-center gap-2">
        {!isRdv && b.type !== "shooting" && (
          <>
            <Button variant="outline" size="sm" data-testid="drawer-edit" onClick={() => onEdit(b)} className="gap-1.5 rounded-xl">
              <Pencil className="w-3.5 h-3.5" /> Modifier
            </Button>
            <Button variant="outline" size="sm" data-testid="drawer-delete" onClick={() => onDeleteBlock(b)}
              className="gap-1.5 rounded-xl text-red-600 hover:text-red-700 ml-auto">
              <Trash2 className="w-3.5 h-3.5" /> Supprimer
            </Button>
          </>
        )}
        {!isRdv && b.type === "shooting" && (
          <p className="text-[11px] text-neutral-400 ml-auto" data-testid="drawer-shooting-hint">Créneau géré depuis Pilotage de contenu → Shootings</p>
        )}
        {isRdv && (
          <Button size="sm" data-testid="drawer-goto-rdv" onClick={() => onOpenFiche?.(rdv)}
            className="ml-auto bg-[#0B132B] hover:bg-[#1C2A4F] text-white gap-1.5 rounded-xl">
            Ouvrir la fiche RDV <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        )}
        {!isRdv && detail?.appointment && (
          <Button variant="outline" size="sm" data-testid="drawer-open-rdv-fiche" onClick={() => onOpenFiche?.(detail.appointment)}
            className="gap-1.5 rounded-xl">
            Fiche RDV <ChevronRight className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>
    </div>
  );
}

/* ================= Page Agenda ================= */
export default function Agenda() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [week, setWeek] = useState(() => {
    const w = searchParams.get("week");
    if (w) { try { const d = parseISO(w); if (!isNaN(d)) return d; } catch { /* ignore */ } }
    return new Date();
  });
  const [view, setView] = useState("week"); // day | week | month
  const [data, setData] = useState(null);
  const [clients, setClients] = useState([]);
  const [rdvs, setRdvs] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [hiddenAgendas, setHiddenAgendas] = useState([]);
  const [fType, setFType] = useState("");
  const [fClient, setFClient] = useState("");
  const [drawer, setDrawer] = useState(null); // { block: detail } | { rdv }
  const [detail, setDetail] = useState(null);
  const [blockModal, setBlockModal] = useState(false);
  const [editBlock, setEditBlock] = useState(null);
  const [absModal, setAbsModal] = useState(false);
  const [form, setForm] = useState({ title: "", type: "internal_task", client_id: "", task_id: "", date: "", start: "09:00", end: "10:00", description: "" });
  const [clientTasks, setClientTasks] = useState([]);
  const [absForm, setAbsForm] = useState({ from: "", to: "", motif: "conges", label: "", full_day: true, start_time: "09:00", end_time: "12:00" });
  const [newTask, setNewTask] = useState("");
  const [ficheRdv, setFicheRdv] = useState(null);
  const highlight = searchParams.get("highlight");

  const load = () => api.get(`/agenda?week=${iso(week)}`).then((r) => setData(r.data)).catch(() => {});
  const reloadAll = () => {
    load();
    api.get("/appointments").then((r) => {
      const list = r.data || [];
      setRdvs(list);
      setFicheRdv((cur) => (cur ? list.find((x) => x.id === cur.id) || cur : cur));
    }).catch(() => {});
  };
  useEffect(() => { load(); }, [week]);
  useEffect(() => { api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/appointments").then((r) => setRdvs(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { api.get("/tasks").then((r) => setTasks(r.data || [])).catch(() => {}); }, []);
  // Lien profond : ?new=1&client= → modal création pré-rempli
  useEffect(() => {
    if (searchParams.get("new") === "1") {
      setForm((f) => ({ ...f, date: iso(new Date()), client_id: searchParams.get("client") || "" }));
      setBlockModal(true);
    }
  }, []);
  useEffect(() => {
    if (highlight) {
      api.get(`/agenda/blocks/${highlight}/detail`).then((r) => { setDetail(r.data); setDrawer({ block: true }); }).catch(() => {});
    }
  }, [highlight]);
  useEffect(() => {
    if (form.client_id) api.get(`/tasks?client_id=${form.client_id}`).then((r) => setClientTasks(r.data || [])).catch(() => setClientTasks([]));
    else setClientTasks([]);
  }, [form.client_id]);

  const clientById = useMemo(() => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]);
  const blocks = useMemo(() => (data?.blocks || []).filter((b) => {
    const ag = agendaOf(b, clientById);
    if (hiddenAgendas.includes(ag)) return false;
    if (fType && b.type !== fType) return false;
    if (fClient && b.client_id !== fClient) return false;
    return true;
  }), [data, hiddenAgendas, fType, fClient, clientById]);

  const weekStart = startOfWeek(week, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const shownDays = view === "day" ? [week] : weekDays;

  /* Grille : bornes depuis work_hours (défaut 8h-19h), pause déjeuner grisée */
  const wh = data?.work_hours || {};
  const bounds = useMemo(() => {
    let min = 24 * 60, max = 0;
    Object.values(wh).forEach((d) => (d?.ranges || []).forEach(([s, e]) => { min = Math.min(min, toMin(s)); max = Math.max(max, toMin(e)); }));
    if (min >= max) { min = 8 * 60; max = 19 * 60; }
    return { start: Math.min(min, 8 * 60), end: Math.max(max, 19 * 60) };
  }, [wh]);

  /* Drag & drop façon iCloud : offset de saisie compensé, aimantation 5 min, fantôme temps réel */
  const [dragBlock, setDragBlock] = useState(null);
  const [ghost, setGhost] = useState(null); // { day, start }
  const targetFromEvent = (e, b) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const dur = b.end_min - b.start_min;
    const raw = bounds.start + (y / HOUR_H) * 60 - (b.grabOffsetMin || 0);
    return Math.min(Math.max(bounds.start, Math.round(raw)), bounds.end - dur);
  };
  const onDragStart = (e, b) => {
    const grabOffsetMin = ((e.clientY - e.currentTarget.getBoundingClientRect().top) / HOUR_H) * 60;
    const payload = { ...b, grabOffsetMin };
    e.dataTransfer.setData("text/plain", JSON.stringify(payload));
    e.dataTransfer.effectAllowed = "move";
    setDragBlock(payload);
    setGhost(null);
  };
  const onDragEnd = () => { setDragBlock(null); setGhost(null); };
  const onDragOver = (e, day) => {
    e.preventDefault();
    if (!dragBlock) return;
    e.dataTransfer.dropEffect = "move";
    setGhost({ day: iso(day), start: targetFromEvent(e, dragBlock) });
  };
  const doMove = (b, day, start, confirmAfter = false) => {
    const dur = b.end_min - b.start_min;
    const body = { date: day, start_min: start, end_min: start + dur };
    if (confirmAfter) body.confirm_after_deadline = true;
    return api.patch(`/agenda/blocks/${b.id}`, body);
  };
  const onDrop = async (e, day) => {
    e.preventDefault();
    const raw = e.dataTransfer.getData("text/plain");
    setDragBlock(null);
    setGhost(null);
    if (!raw) return;
    const b = JSON.parse(raw);
    const start = targetFromEvent(e, b);
    if (b.appointment_id) {
      // RDV : on met à jour le rendez-vous (le bloc agenda est resynchronisé côté serveur, trajets inclus)
      const appt = rdvs.find((x) => x.id === b.appointment_id);
      const tb = appt ? Math.max(0, (appt.start_min ?? b.start_min) - b.start_min) : 0;
      const prev = appt ? { date: appt.date, start_time: toHHMM(appt.start_min) } : null;
      try {
        await api.patch(`/appointments/${b.appointment_id}`, { date: iso(day), start_time: toHHMM(start + tb) });
        toast.success("Rendez-vous déplacé", prev ? {
          action: { label: "Annuler", onClick: async () => { await api.patch(`/appointments/${b.appointment_id}`, prev).catch(() => {}); reloadAll(); } },
        } : undefined);
        reloadAll();
      } catch (err) { toast.error(apiError(err)); }
      return;
    }
    try {
      await doMove(b, iso(day), start);
      toast.success("Créneau déplacé", {
        action: { label: "Annuler", onClick: async () => { await doMove(b, b.date, b.start_min, true).catch(() => {}); load(); } },
      });
      load();
    } catch (err) {
      const detail = String(err?.response?.data?.detail || "");
      if (err?.response?.status === 409 && detail.includes("AFTER_DEADLINE")) {
        if (window.confirm("Ce créneau se situe après la deadline de la tâche liée. Déplacer quand même ?")) {
          try {
            await doMove(b, iso(day), start, true);
            toast.success("Créneau déplacé");
            load();
          } catch (err2) { toast.error(apiError(err2)); }
        }
      } else {
        toast.error(apiError(err));
      }
    }
  };

  const onResizeBlock = async (b, newEnd) => {
    try {
      if (b.appointment_id) {
        const appt = rdvs.find((x) => x.id === b.appointment_id);
        if (!appt) throw new Error("RDV introuvable");
        const ta = Math.max(0, b.end_min - (appt.start_min + (appt.rdv_minutes || 60)));
        const newRdvMin = Math.max(15, newEnd - appt.start_min - ta);
        if (newRdvMin === (appt.rdv_minutes || 60)) return;
        await api.patch(`/appointments/${appt.id}`, { rdv_minutes: newRdvMin });
      } else {
        if (newEnd === b.end_min) return;
        await api.patch(`/agenda/blocks/${b.id}`, { end_min: newEnd });
      }
      toast.success("Durée mise à jour");
      reloadAll();
    } catch (err) { toast.error(apiError(err)); }
  };

  const openBlockDetail = async (b) => {
    setDrawer({ block: true });
    setDetail(null);
    try { const { data: d } = await api.get(`/agenda/blocks/${b.id}/detail`); setDetail(d); }
    catch { toast.error("Bloc introuvable"); setDrawer(null); }
  };

  /* Clic sur un bloc : RDV (meeting avec appointment) → grande fiche ; tâche simple → petit drawer */
  const onBlockClick = (b) => {
    if (b.appointment_id) {
      const r = rdvs.find((x) => x.id === b.appointment_id);
      if (r) { setFicheRdv(r); return; }
      api.get("/appointments").then((res) => {
        const found = (res.data || []).find((x) => x.id === b.appointment_id);
        if (found) setFicheRdv(found); else openBlockDetail(b);
      }).catch(() => openBlockDetail(b));
      return;
    }
    openBlockDetail(b);
  };

  const delBlock = async (b) => {
    await api.delete(`/agenda/blocks/${b.id}`);
    toast.success("Bloc supprimé");
    setDrawer(null);
    load();
  };
  const completeTask = async (t) => {
    await api.patch(`/tasks/${t.id}`, { status: "finalise" });
    toast.success("Tâche finalisée");
    api.get("/tasks").then((r) => setTasks(r.data || [])).catch(() => {});
    if (drawer?.block && detail?.block) openBlockDetail(detail.block);
  };

  const openEdit = (b) => {
    setEditBlock(b);
    setForm({ title: b.title || "", type: b.type, client_id: b.client_id || "", task_id: b.task_id || "", date: b.date, start: toHHMM(b.start_min), end: toHHMM(b.end_min), description: b.description || "" });
    setBlockModal(true);
  };

  const submitBlock = async () => {
    if (!form.date) { toast.error("Indiquez la date"); return; }
    if (!form.title.trim() && !form.task_id) { toast.error("Indiquez un titre ou sélectionnez une tâche"); return; }
    try {
      if (editBlock) {
        await api.patch(`/agenda/blocks/${editBlock.id}`, {
          title: form.title.trim(), type: form.type, client_id: form.client_id || null,
          task_id: form.task_id || null, description: form.description, date: form.date,
          start_min: toMin(form.start), end_min: toMin(form.end),
        });
        toast.success("Créneau mis à jour");
      } else {
        const { data: created } = await api.post("/agenda/blocks", {
          title: form.title.trim(), type: form.type, client_id: form.client_id || null,
          task_id: form.task_id || null, description: form.description, date: form.date,
          start_min: toMin(form.start), end_min: toMin(form.end),
        });
        toast.success(form.task_id ? "Créneau bloqué — la tâche passe à « Programmé »" : "Bloc ajouté à l'agenda");
        if (created?.overlap_warning) toast.warning("Attention : ce créneau en chevauche un autre — les deux restent visibles côte à côte");
        if (!form.task_id && created?.id) {
          const blockRef = created;
          const titleRef = form.title.trim();
          toast.info("Créer également une tâche associée ?", {
            duration: 8000,
            action: {
              label: "Créer la tâche",
              onClick: async () => {
                try {
                  const { data: t } = await api.post("/tasks", {
                    title: titleRef, client_id: blockRef.client_id, deadline: blockRef.date,
                    estimate_min: blockRef.duration_minutes, source_type: "agenda_event",
                    source_id: blockRef.id, source_label: `Agenda — ${blockRef.title}`, source_url: "/agenda",
                  });
                  await api.patch(`/agenda/blocks/${blockRef.id}`, { task_id: t.id });
                  toast.success("Tâche créée et reliée à l'événement");
                  window.dispatchEvent(new Event("sf:tasks"));
                  load();
                } catch { toast.error("Création de la tâche impossible"); }
              },
            },
          });
        }
      }
      setBlockModal(false); setEditBlock(null);
      setForm({ title: "", type: "internal_task", client_id: "", task_id: "", date: "", start: "09:00", end: "10:00", description: "" });
      setDrawer(null);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const createAbsence = async () => {
    if (!absForm.from || !absForm.to) { toast.error("Indiquez la période"); return; }
    if (absForm.motif === "autre" && !absForm.label.trim()) { toast.error("Précisez le motif"); return; }
    if (!absForm.full_day && (!absForm.start_time || !absForm.end_time)) { toast.error("Indiquez la plage horaire"); return; }
    try {
      await api.post("/agenda/absence", absForm);
      toast.success("Indisponibilité ajoutée");
      setAbsModal(false);
      setAbsForm({ from: "", to: "", motif: "conges", label: "", full_day: true, start_time: "09:00", end_time: "12:00" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const addQuickTask = async () => {
    if (!newTask.trim()) return;
    try {
      await api.post("/tasks", { title: newTask.trim(), deadline: iso(new Date()) });
      setNewTask("");
      api.get("/tasks").then((r) => setTasks(r.data || [])).catch(() => {});
      toast.success("Tâche ajoutée");
    } catch (err) { toast.error(apiError(err)); }
  };

  /* RDV à venir + To-do du jour */
  const todayIso = iso(new Date());
  const upcomingRdvs = rdvs.filter((r) => r.date && r.date >= todayIso && r.booking_status !== "annule")
    .sort((a, b) => (a.date + String(a.start_min)).localeCompare(b.date + String(b.start_min))).slice(0, 6);
  const rdvDayLabel = (r) => (r.date === todayIso ? "Aujourd'hui" : r.date === iso(addDays(new Date(), 1)) ? "Demain" : safeFmt(r.date, "d MMM"));

  const activeTasks = tasks.filter((t) => !t.archived && !["finalise", "livre_client"].includes(t.status));
  const deadlineTasks = activeTasks.filter((t) => t.deadline && t.deadline.slice(0, 10) <= todayIso)
    .sort((a, b) => a.deadline.localeCompare(b.deadline));
  const todoToday = (deadlineTasks.length ? deadlineTasks : activeTasks.filter((t) => !t.deadline)).slice(0, 5);

  const periodLabel = view === "month"
    ? format(week, "MMMM yyyy", { locale: fr })
    : view === "day"
      ? format(week, "EEEE d MMMM yyyy", { locale: fr })
      : `${format(weekDays[0], "d", { locale: fr })} – ${format(weekDays[6], "d MMMM yyyy", { locale: fr })}`;
  const navStep = (dir) => setWeek((w) => (view === "month" ? addMonths(w, dir) : view === "day" ? addDays(w, dir) : addDays(w, dir * 7)));

  const summary = data?.summary;
  const hours = [];
  for (let h = bounds.start; h < bounds.end; h += 60) hours.push(h);

  return (
    <div className="space-y-3" data-testid="agenda-page">
      {/* En-tête */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A]">Agenda</h1>
          <p className="text-neutral-500 text-xs mt-1">Organisez vos rendez-vous, tâches et événements d'équipe.</p>
        </div>
        <p className="text-[#64748B] italic text-sm leading-snug text-right hidden md:block">« Du temps bien organisé<br />pour des projets qui avancent. »</p>
      </div>

      {/* Barre d'outils */}
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="week-today" onClick={() => setWeek(new Date())}
          className="text-xs font-medium border border-[#E2E8F0] bg-white rounded-lg px-3 py-1.5 hover:bg-neutral-50 transition-colors">Aujourd'hui</button>
        <button data-testid="week-prev" onClick={() => navStep(-1)} className="p-1.5 rounded-lg border border-[#E2E8F0] bg-white hover:bg-neutral-50 text-neutral-500"><ChevronLeft className="w-4 h-4" /></button>
        <button data-testid="week-next" onClick={() => navStep(1)} className="p-1.5 rounded-lg border border-[#E2E8F0] bg-white hover:bg-neutral-50 text-neutral-500"><ChevronRight className="w-4 h-4" /></button>
        <p className="font-heading font-bold text-sm text-[#0F172A] capitalize px-1" data-testid="agenda-period-label">{periodLabel}</p>
        <div className="flex rounded-full border border-[#E2E8F0] bg-white p-0.5 ml-auto" data-testid="agenda-view-toggle">
          {[["day", "Jour"], ["week", "Semaine"], ["month", "Mois"]].map(([v, l]) => (
            <button key={v} data-testid={`agenda-view-${v}`} onClick={() => setView(v)}
              className={`text-xs font-medium rounded-full px-3.5 py-1.5 transition-colors ${view === v ? "bg-[#0B132B] text-white" : "text-neutral-500 hover:text-neutral-900"}`}>{l}</button>
          ))}
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="agenda-new-block" className="inline-flex items-center gap-1.5 bg-[#0B132B] text-white rounded-xl px-4 py-2 text-xs font-semibold hover:bg-[#1C2A4F] transition-colors">
              <Plus className="w-3.5 h-3.5" /> Ajouter un événement
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem data-testid="agenda-add-creneau" onClick={() => { setEditBlock(null); setForm((f) => ({ ...f, date: iso(week) })); setBlockModal(true); }}>Créneau de travail</DropdownMenuItem>
            <DropdownMenuItem data-testid="agenda-add-absence" onClick={() => setAbsModal(true)}>Indisponibilité / absence</DropdownMenuItem>
            <DropdownMenuItem data-testid="agenda-add-rdv" onClick={() => navigate("/rdv")}>Rendez-vous client</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className={`grid grid-cols-1 gap-3 items-start transition-all duration-300 ${ficheRdv ? "xl:grid-cols-[minmax(0,1fr)_minmax(520px,44%)]" : "xl:grid-cols-[230px_1fr_280px]"}`}>
        {/* Colonne gauche */}
        {!ficheRdv && (
        <div className="space-y-3">
          <MiniCalendar cursor={week} selected={week} onPick={(d, monthOnly) => setWeek(d)} />

          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3" data-testid="agenda-my-calendars">
            <p className="font-heading font-bold text-[13px] text-[#0F172A] mb-2">Mes agendas</p>
            {Object.entries(AGENDAS).map(([id, ag]) => (
              <label key={id} className="flex items-center gap-2 py-1 cursor-pointer text-xs text-neutral-600 hover:text-neutral-900">
                <input type="checkbox" checked={!hiddenAgendas.includes(id)} data-testid={`agenda-filter-${id}`}
                  onChange={() => setHiddenAgendas((h) => (h.includes(id) ? h.filter((x) => x !== id) : [...h, id]))}
                  className="w-3.5 h-3.5 rounded accent-[#0B132B]" />
                <span className="w-2 h-2 rounded-full" style={{ background: ag.dot }} />
                {ag.label}
              </label>
            ))}
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3 space-y-2" data-testid="agenda-filters">
            <p className="font-heading font-bold text-[13px] text-[#0F172A]">Filtrer par</p>
            <select data-testid="agenda-filter-type" value={fType} onChange={(e) => setFType(e.target.value)}
              className="w-full text-xs border border-[#E2E8F0] rounded-lg px-2.5 py-1.5 bg-white text-neutral-600">
              <option value="">Tous les types</option>
              {Object.entries(BLOCK_TYPE_LABELS).map(([id, l]) => <option key={id} value={id}>{l}</option>)}
            </select>
            <select data-testid="agenda-filter-client" value={fClient} onChange={(e) => setFClient(e.target.value)}
              className="w-full text-xs border border-[#E2E8F0] rounded-lg px-2.5 py-1.5 bg-white text-neutral-600">
              <option value="">Tous les clients</option>
              {clients.filter((c) => !c.archived).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>

          {summary && (
            <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm p-3" data-testid="agenda-summary">
              <div className="flex items-center justify-between text-[11px] mb-1.5">
                <span className="font-semibold text-[#0F172A]">Capacité semaine</span>
                <span className={`font-bold ${summary.charge_pct >= 90 ? "text-[#BE123C]" : summary.charge_pct >= 70 ? "text-[#B45309]" : "text-[#15803D]"}`} data-testid="agenda-charge-pct">{summary.charge_pct} %</span>
              </div>
              <div className="h-1.5 bg-neutral-100 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${summary.charge_pct >= 90 ? "bg-[#FB7185]" : summary.charge_pct >= 70 ? "bg-[#FBBF24]" : "bg-[#4ADE80]"}`}
                  style={{ width: `${Math.min(100, summary.charge_pct)}%` }} data-testid="agenda-charge-bar" />
              </div>
              <p className="text-[10px] text-neutral-400 mt-1.5">{Math.floor(summary.free_min / 60)} h dispo · {Math.floor(summary.recurring_min / 60)} h récurrent</p>
            </div>
          )}
        </div>
        )}

        {/* Grille centrale */}
        <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden" data-testid="agenda-grid">
          {view !== "month" ? (
            <>
              <div className={`grid border-b border-neutral-100`} style={{ gridTemplateColumns: `44px repeat(${shownDays.length}, 1fr)` }}>
                <div />
                {shownDays.map((d) => (
                  <div key={d.toISOString()} className={`py-2 text-center ${isToday(d) ? "bg-[#DBEAFE]/40" : ""}`}>
                    <p className={`text-[10px] font-semibold uppercase ${isToday(d) ? "text-[#1D4ED8]" : "text-neutral-400"}`}>{format(d, "EEE", { locale: fr })}</p>
                    <p className={`text-sm font-bold ${isToday(d) ? "inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#3B82F6] text-white" : "text-[#0F172A]"}`}>{format(d, "d")}</p>
                  </div>
                ))}
              </div>
              <div className="overflow-y-auto" style={{ maxHeight: "calc(100vh - 320px)" }}>
                <div className="grid" style={{ gridTemplateColumns: `44px repeat(${shownDays.length}, 1fr)` }}>
                  <div className="relative" style={{ height: ((bounds.end - bounds.start) / 60) * HOUR_H }}>
                    {hours.map((h) => (
                      <p key={h} className="absolute right-1.5 text-[9px] text-neutral-400 font-medium" style={{ top: ((h - bounds.start) / 60) * HOUR_H - 6 }}>{toHHMM(h)}</p>
                    ))}
                  </div>
                  {shownDays.map((d) => {
                    const dayBlocks = blocks.filter((b) => b.date === iso(d));
                    const dow = String((d.getDay() + 6) % 7);
                    const lunch = (wh[dow]?.ranges?.length === 2) ? [toMin(wh[dow].ranges[0][1]), toMin(wh[dow].ranges[1][0])] : null;
                    return (
                      <div key={d.toISOString()} data-testid={`agenda-day-${iso(d)}`}
                        onDragOver={(e) => onDragOver(e, d)}
                        onDrop={(e) => onDrop(e, d)}
                        onClick={() => { setEditBlock(null); setForm((f) => ({ ...f, date: iso(d) })); }}
                        className={`relative border-l border-neutral-100 ${isToday(d) ? "bg-[#DBEAFE]/20" : ""}`}
                        style={{ height: ((bounds.end - bounds.start) / 60) * HOUR_H }}>
                        {hours.map((h) => <span key={h} className="absolute left-0 right-0 border-t border-neutral-50" style={{ top: ((h - bounds.start) / 60) * HOUR_H }} />)}
                        {lunch && (
                          <div className="absolute left-0 right-0 bg-neutral-50/80 border-y border-dashed border-neutral-200 flex items-center justify-center"
                            style={{ top: ((lunch[0] - bounds.start) / 60) * HOUR_H, height: ((lunch[1] - lunch[0]) / 60) * HOUR_H }}>
                            <span className="text-[9px] text-neutral-400">Pause déjeuner</span>
                          </div>
                        )}
                        {isToday(d) && <NowLine bounds={bounds} />}
                        {ghost?.day === iso(d) && dragBlock && (() => {
                          const gAg = AGENDAS[agendaOf(dragBlock, clientById)];
                          return (
                            <div data-testid="agenda-drop-ghost"
                              className="absolute left-0.5 right-0.5 rounded-lg border-2 border-dashed pointer-events-none z-10"
                              style={{
                                top: ((ghost.start - bounds.start) / 60) * HOUR_H,
                                height: Math.max(24, ((dragBlock.end_min - dragBlock.start_min) / 60) * HOUR_H),
                                borderColor: gAg.dot, background: `${gAg.bg}CC`,
                              }}>
                              <span className="text-[9px] font-bold px-1" style={{ color: gAg.text }}>
                                {toHHMM(ghost.start)} – {toHHMM(ghost.start + (dragBlock.end_min - dragBlock.start_min))}
                              </span>
                            </div>
                          );
                        })()}
                        {dayBlocks.map((b) => (
                          <BlockCard key={b.id} b={b} dayStart={bounds.start} ag={AGENDAS[agendaOf(b, clientById)]}
                            onOpen={onBlockClick} onDragStart={onDragStart} onDragEnd={onDragEnd} onResize={onResizeBlock}
                            highlighted={b.id === highlight || (!!ficheRdv && b.appointment_id === ficheRdv.id)} dimmed={dragBlock?.id === b.id} />
                        ))}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <MonthView week={week} blocks={blocks} agendaOf={(b) => agendaOf(b, clientById)} onPickDay={(d) => { setWeek(d); setView("day"); }} />
          )}
        </div>

        {/* Colonne droite */}
        {!ficheRdv && (
        <div className="space-y-3">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden" data-testid="agenda-upcoming-rdv">
            <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-neutral-100">
              <Video className="w-4 h-4 text-[#0F172A]" />
              <h3 className="font-heading font-bold text-[13px] text-[#0F172A]">Rendez-vous à venir</h3>
              <Link to="/rdv" className="ml-auto text-[10px] font-semibold text-[#0369A1] hover:underline">Voir tous ›</Link>
            </div>
            {upcomingRdvs.length === 0 && <p className="px-3.5 py-4 text-xs text-neutral-400">Aucun RDV à venir.</p>}
            {upcomingRdvs.map((r) => (
              <button key={r.id} data-testid={`agenda-rdv-${r.id}`} onClick={() => setFicheRdv(r)}
                className="w-full flex items-start gap-2.5 px-3.5 py-2.5 border-b border-neutral-100 last:border-0 hover:bg-neutral-50 transition-colors text-left">
                <span className="w-2 h-2 rounded-full mt-1.5 shrink-0 bg-[#3B82F6]" />
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] font-semibold text-[#1D4ED8]">{rdvDayLabel(r)} – {toHHMM(r.start_min)}</p>
                  <p className="text-xs font-medium text-[#0F172A] truncate">{r.title}</p>
                  <p className="text-[10px] text-neutral-400 truncate">{r.type_label}{r.company ? ` · ${r.company}` : ""}</p>
                </div>
                <span className="w-6 h-6 rounded-full bg-neutral-100 text-[9px] font-bold text-neutral-500 flex items-center justify-center shrink-0">{initials(r.contact_name || r.company)}</span>
              </button>
            ))}
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-2xl shadow-sm overflow-hidden" data-testid="agenda-todo">
            <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-neutral-100">
              <CheckCircle2 className="w-4 h-4 text-[#0F172A]" />
              <h3 className="font-heading font-bold text-[13px] text-[#0F172A]">To-do du jour</h3>
              <Link to="/taches" className="ml-auto text-[10px] font-semibold text-[#0369A1] hover:underline">Voir toutes ›</Link>
            </div>
            {todoToday.length === 0 && <p className="px-3.5 py-4 text-xs text-neutral-400">Aucune tâche — belle journée !</p>}
            {todoToday.map((t) => (
              <div key={t.id} className="flex items-center gap-2.5 px-3.5 py-2 border-b border-neutral-100 last:border-0" data-testid={`agenda-todo-${t.id}`}>
                <button onClick={() => completeTask(t)} data-testid={`agenda-todo-check-${t.id}`}
                  className="w-4 h-4 rounded-full border-2 border-neutral-300 hover:border-[#15803D] hover:bg-[#DCFCE7] transition-colors shrink-0" title="Marquer finalisée" />
                <p className="flex-1 min-w-0 text-xs text-[#0F172A] truncate">{t.title}</p>
                {t.deadline && (
                  <span className={`text-[9px] whitespace-nowrap ${isPast(parseISO(t.deadline.slice(0, 10))) && !isToday(parseISO(t.deadline.slice(0, 10))) ? "text-[#BE123C] font-semibold" : "text-neutral-400"}`}>
                    {isToday(parseISO(t.deadline.slice(0, 10))) ? "Auj." : safeFmt(t.deadline, "d MMM")}
                  </span>
                )}
              </div>
            ))}
            <div className="flex items-center gap-1.5 px-3.5 py-2 border-t border-neutral-100">
              <Plus className="w-3.5 h-3.5 text-neutral-300 shrink-0" />
              <input data-testid="agenda-todo-add" value={newTask} onChange={(e) => setNewTask(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addQuickTask()}
                placeholder="Ajouter une tâche…" className="flex-1 text-xs py-1 focus:outline-none placeholder:text-neutral-300" />
            </div>
          </div>
        </div>
        )}

        {/* Fiche RDV détaillée (panneau latéral, ~40-45 %) */}
        {ficheRdv && (
          <RdvFiche variant="panel" rdv={ficheRdv} onClose={() => setFicheRdv(null)} onChanged={reloadAll} />
        )}
      </div>

      {/* Drawer détail */}
      {drawer && (
        <DetailDrawer
          detail={detail} rdv={drawer.rdv} clientById={clientById}
          onClose={() => { setDrawer(null); setDetail(null); }}
          onEdit={openEdit}
          onDeleteBlock={delBlock}
          onCompleteTask={completeTask}
          onReload={load}
          onOpenFiche={(r) => { setDrawer(null); setDetail(null); setFicheRdv(r); }}
        />
      )}

      {/* Fiche RDV : rendue en panneau latéral dans la grille ci-dessus */}

      {/* Modal créneau */}
      <Dialog open={blockModal} onOpenChange={(v) => { setBlockModal(v); if (!v) setEditBlock(null); }}>
        <DialogContent className="max-w-md" data-testid="block-modal" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">{editBlock ? "Modifier le créneau" : "Ajouter un créneau"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Titre</Label>
              <Input data-testid="block-title" className="mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex. Création visuels Instagram" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Type</Label>
                <select data-testid="block-type" className="mt-1 w-full text-sm border border-[#E2E8F0] rounded-lg px-2.5 py-2 bg-white" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  {Object.entries(BLOCK_TYPE_LABELS).filter(([id]) => id !== "unavailable").map(([id, l]) => <option key={id} value={id}>{l}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Client</Label>
                <select data-testid="block-client" className="mt-1 w-full text-sm border border-[#E2E8F0] rounded-lg px-2.5 py-2 bg-white" value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value, task_id: "" })}>
                  <option value="">Aucun</option>
                  {clients.filter((c) => !c.archived).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            {form.client_id && clientTasks.length > 0 && (
              <div>
                <Label className="text-xs">Lier une tâche (optionnel)</Label>
                <select data-testid="block-task" className="mt-1 w-full text-sm border border-[#E2E8F0] rounded-lg px-2.5 py-2 bg-white" value={form.task_id} onChange={(e) => setForm({ ...form, task_id: e.target.value })}>
                  <option value="">Aucune</option>
                  {clientTasks.filter((t) => !t.archived).map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Date</Label>
                <Input data-testid="block-date" type="date" className="mt-1" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Début</Label>
                <Input data-testid="block-start" type="time" className="mt-1" value={form.start} onChange={(e) => setForm({ ...form, start: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">Fin</Label>
                <Input data-testid="block-end" type="time" className="mt-1" value={form.end} onChange={(e) => setForm({ ...form, end: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea data-testid="block-description" rows={2} className="mt-1" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <Button data-testid="block-submit" onClick={submitBlock} className="w-full bg-[#0B132B] hover:bg-[#1C2A4F] text-white rounded-xl">
              {editBlock ? "Enregistrer les modifications" : "Bloquer ce temps"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal absence */}
      <Dialog open={absModal} onOpenChange={setAbsModal}>
        <DialogContent className="max-w-md" data-testid="absence-modal" aria-describedby={undefined}>
          <DialogHeader><DialogTitle className="font-heading">Indisponibilité</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Du</Label><Input data-testid="abs-from" type="date" className="mt-1" value={absForm.from} onChange={(e) => setAbsForm({ ...absForm, from: e.target.value })} /></div>
              <div><Label className="text-xs">Au</Label><Input data-testid="abs-to" type="date" className="mt-1" value={absForm.to} onChange={(e) => setAbsForm({ ...absForm, to: e.target.value })} /></div>
            </div>
            <div>
              <Label className="text-xs">Motif</Label>
              <select data-testid="abs-motif" className="mt-1 w-full text-sm border border-[#E2E8F0] rounded-lg px-2.5 py-2 bg-white" value={absForm.motif} onChange={(e) => setAbsForm({ ...absForm, motif: e.target.value })}>
                <option value="conges">Congés</option>
                <option value="maladie">Maladie</option>
                <option value="formation">Formation</option>
                <option value="autre">Autre</option>
              </select>
            </div>
            {absForm.motif === "autre" && (
              <div><Label className="text-xs">Précisez</Label><Input data-testid="abs-label" className="mt-1" value={absForm.label} onChange={(e) => setAbsForm({ ...absForm, label: e.target.value })} /></div>
            )}
            <label className="flex items-center gap-2 text-xs text-neutral-600">
              <input type="checkbox" checked={absForm.full_day} onChange={(e) => setAbsForm({ ...absForm, full_day: e.target.checked })} data-testid="abs-fullday" /> Journée(s) entière(s)
            </label>
            {!absForm.full_day && (
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">De</Label><Input data-testid="abs-start" type="time" className="mt-1" value={absForm.start_time} onChange={(e) => setAbsForm({ ...absForm, start_time: e.target.value })} /></div>
                <div><Label className="text-xs">À</Label><Input data-testid="abs-end" type="time" className="mt-1" value={absForm.end_time} onChange={(e) => setAbsForm({ ...absForm, end_time: e.target.value })} /></div>
              </div>
            )}
            <Button data-testid="abs-submit" onClick={createAbsence} className="w-full bg-[#0B132B] hover:bg-[#1C2A4F] text-white rounded-xl">Enregistrer l'indisponibilité</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* Ligne « maintenant » rouge sur la colonne du jour */
function NowLine({ bounds }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const i = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(i); }, []);
  const mins = now.getHours() * 60 + now.getMinutes();
  if (mins < bounds.start || mins > bounds.end) return null;
  return (
    <div className="absolute left-0 right-0 z-10 pointer-events-none flex items-center" style={{ top: ((mins - bounds.start) / 60) * HOUR_H }} data-testid="agenda-now-line">
      <span className="w-2 h-2 rounded-full bg-[#F43F5E] -ml-1" />
      <span className="flex-1 h-0.5 bg-[#F43F5E]" />
    </div>
  );
}

/* Vue Mois : grille compacte avec pastilles par agenda */
function MonthView({ week, blocks, agendaOf, onPickDay }) {
  const mStart = startOfMonth(week);
  const days = [];
  let d = startOfWeek(mStart, { weekStartsOn: 1 });
  const end = endOfWeek(endOfMonth(week), { weekStartsOn: 1 });
  while (d <= end) { days.push(d); d = addDays(d, 1); }
  return (
    <div data-testid="agenda-month-grid">
      <div className="grid grid-cols-7 border-b border-neutral-100">
        {["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"].map((x) => (
          <p key={x} className="py-2 text-center text-[10px] font-semibold uppercase text-neutral-400">{x}</p>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const dayBlocks = blocks.filter((b) => b.date === iso(day));
          const inMonth = isSameMonth(day, week);
          return (
            <button key={day.toISOString()} data-testid={`agenda-month-day-${iso(day)}`} onClick={() => onPickDay(day)}
              className={`min-h-[72px] border-b border-r border-neutral-100 p-1.5 text-left align-top hover:bg-neutral-50 transition-colors ${!inMonth ? "bg-neutral-50/50" : ""} ${isToday(day) ? "bg-[#DBEAFE]/30" : ""}`}>
              <span className={`text-[11px] font-semibold w-5 h-5 inline-flex items-center justify-center rounded-full ${isToday(day) ? "bg-[#0B132B] text-white" : inMonth ? "text-neutral-600" : "text-neutral-300"}`}>
                {format(day, "d")}
              </span>
              <div className="flex flex-wrap gap-1 mt-1">
                {[...new Set(dayBlocks.map((b) => agendaOf(b)))].map((ag) => (
                  <span key={ag} className="w-2 h-2 rounded-full" style={{ background: AGENDAS[ag].dot }} title={AGENDAS[ag].label} />
                ))}
                {dayBlocks.length > 0 && <span className="text-[9px] text-neutral-400">{dayBlocks.length}</span>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
